'use client';

import React, { useRef, useState } from 'react';
import { X, Download, Share2, Award, CheckCircle2 } from 'lucide-react';
import { Linkedin } from 'lucide-react';

interface CertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  learnerName: string;
  subjectTitle: string;
  completionDate: string;
  avgScore: number;
  studentId?: string | null;
}

export default function CertificateModal({
  isOpen,
  onClose,
  learnerName,
  subjectTitle,
  completionDate,
  avgScore,
  studentId,
}: CertificateModalProps) {
  const certRef = useRef<HTMLDivElement>(null);
  const [sharing, setSharing] = useState(false);

  if (!isOpen) return null;

  const certId = studentId
    ? `IBA-${studentId}-${subjectTitle.slice(0, 3).toUpperCase()}`
    : `IBA-${Date.now().toString(36).toUpperCase()}`;

  const linkedInShareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
    'https://ibrain.academy'
  )}&title=${encodeURIComponent(
    `I completed ${subjectTitle} at iBrain Academy`
  )}&summary=${encodeURIComponent(
    `I just earned a certificate in ${subjectTitle} from iBrain Academy — an AI-guided adaptive learning platform. Achieved ${avgScore}% average score. #iBrainAcademy #OnlineLearning #AI`
  )}&source=${encodeURIComponent('iBrain Academy')}`;

  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `🎓 Just completed ${subjectTitle} at @iBrainAcademy with ${avgScore}% avg score! AI-guided adaptive learning is the future. #iBrainAcademy #Learning`
  )}&url=${encodeURIComponent('https://ibrain.academy')}`;

  const handlePrint = () => {
    const printContent = certRef.current;
    if (!printContent) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>Certificate — ${learnerName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Plus Jakarta Sans', sans-serif; background: white; }
          </style>
        </head>
        <body>${printContent.outerHTML}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Certificate of Completion — ${subjectTitle}`,
          text: `I completed ${subjectTitle} at iBrain Academy with ${avgScore}% average score!`,
          url: 'https://ibrain.academy',
        });
      } else {
        await navigator.clipboard.writeText(
          `I completed ${subjectTitle} at iBrain Academy with ${avgScore}% average score! https://ibrain.academy`
        );
        alert('Share text copied to clipboard!');
      }
    } catch {
      // user cancelled
    }
    setSharing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            <h2 className="text-base font-semibold text-foreground">Certificate of Completion</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Certificate */}
        <div className="p-6">
          <div
            ref={certRef}
            className="relative bg-white rounded-xl overflow-hidden"
            style={{
              border: '2px solid #F97316',
              boxShadow: '0 0 0 6px #FFF7ED, 0 0 0 8px #F97316',
              padding: '48px 56px',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {/* Decorative corner accents */}
            <div style={{ position: 'absolute', top: 16, left: 16, width: 40, height: 40, borderTop: '3px solid #F97316', borderLeft: '3px solid #F97316', borderRadius: '4px 0 0 0' }} />
            <div style={{ position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderTop: '3px solid #F97316', borderRight: '3px solid #F97316', borderRadius: '0 4px 0 0' }} />
            <div style={{ position: 'absolute', bottom: 16, left: 16, width: 40, height: 40, borderBottom: '3px solid #F97316', borderLeft: '3px solid #F97316', borderRadius: '0 0 0 4px' }} />
            <div style={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, borderBottom: '3px solid #F97316', borderRight: '3px solid #F97316', borderRadius: '0 0 4px 0' }} />

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, background: '#F97316', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: 'white', fontWeight: 800, fontSize: 18 }}>i</span>
                </div>
                <span style={{ fontWeight: 800, fontSize: 20, color: '#1C1917', letterSpacing: '-0.5px' }}>iBrain Academy</span>
              </div>
              <div style={{ width: 60, height: 2, background: '#F97316', margin: '0 auto 16px' }} />
              <p style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#78716C', fontWeight: 600 }}>
                Certificate of Completion
              </p>
            </div>

            {/* Body */}
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <p style={{ fontSize: 13, color: '#78716C', marginBottom: 8 }}>This certifies that</p>
              <h1 style={{ fontSize: 34, fontWeight: 800, color: '#1C1917', letterSpacing: '-1px', marginBottom: 12, lineHeight: 1.1 }}>
                {learnerName}
              </h1>
              <p style={{ fontSize: 13, color: '#78716C', marginBottom: 8 }}>has successfully completed the course</p>
              <div style={{ display: 'inline-block', background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 10, padding: '10px 28px', marginBottom: 16 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#EA580C', margin: 0 }}>{subjectTitle}</h2>
              </div>
              <p style={{ fontSize: 13, color: '#78716C' }}>
                with an average quiz score of{' '}
                <strong style={{ color: '#1C1917' }}>{avgScore}%</strong>
              </p>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px dashed #E7E5E4', marginBottom: 28 }} />

            {/* Footer row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <p style={{ fontSize: 11, color: '#78716C', marginBottom: 4 }}>Date of Completion</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#1C1917' }}>{completionDate}</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', border: '2px solid #F97316', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px' }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#F97316', letterSpacing: 1, textTransform: 'uppercase', lineHeight: 1.3, textAlign: 'center' }}>
                    iBrain<br />Academy<br />Verified
                  </span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 11, color: '#78716C', marginBottom: 4 }}>Certificate ID</p>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#1C1917', fontFamily: 'monospace' }}>{certId}</p>
              </div>
            </div>

            {/* Issuer */}
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #E7E5E4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 11, color: '#78716C' }}>Issued by</p>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#1C1917' }}>iBrain Academy</p>
                <p style={{ fontSize: 11, color: '#78716C' }}>A product of Jawelt Bay GmbH</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 11, color: '#78716C' }}>CEO</p>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#1C1917' }}>Sam Bay</p>
                <p style={{ fontSize: 11, color: '#78716C' }}>Jawelt Bay GmbH</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download / Print
            </button>
            <a
              href={linkedInShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0A66C2] text-white font-semibold text-sm hover:bg-[#0A66C2]/90 transition-colors"
            >
              <Linkedin className="w-4 h-4" />
              Share on LinkedIn
            </a>
            <a
              href={twitterShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#1DA1F2] text-white font-semibold text-sm hover:bg-[#1DA1F2]/90 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share on X
            </a>
            <button
              onClick={handleShare}
              disabled={sharing}
              className="sm:flex-none flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-muted text-foreground font-semibold text-sm hover:bg-muted/80 transition-colors disabled:opacity-60"
            >
              <Share2 className="w-4 h-4" />
              <span className="sm:hidden">More</span>
            </button>
          </div>

          <div className="mt-4 flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-green-700">
              This certificate is issued by <strong>iBrain Academy</strong> (Jawelt Bay GmbH) and verifies your successful completion of <strong>{subjectTitle}</strong>. Add it to your LinkedIn profile under <em>Licenses &amp; Certifications</em>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
